import { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../../context/auth-context';
import PackagesAPI from '../../../services/api/admin/packages-api';
import TradesAPI from '../../../services/api/admin/trades-api';
import ProjectsAPI from '../../../services/api/admin/projects-api';
import Logger from '../../../utils/logger';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import OutlinedInput from '@mui/material/OutlinedInput';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';

export const PackageModal = ({
  packageSelected,
  editPackage,
  open,
  handleCancel,
  handleAddPackage,
  handleUpdatePackage,
  currentProject,
  onTradeAutoCopied,
  onActivitiesAutoCopied,
  onOpenUpdateBudget,
}) => {
  const [currentPackage, setCurrentPackage] = useState({
    id: '',
    name: '',
    autodesk_name: '',
    area: '',
    tracking_status: '',
    status_step_set_id: '',
    archived: false,
    field_phase: '',
    field_cost_code: '',
    units: 0,
    uom: '',
    trade_id: '',
    activity_ids: [],
    include_activities: true,
    default_activity_id: '',
    requires_shop_fabrication: false,
    requires_vdc_design: false,
    is_general_conditions: false,
    is_change_order: false,
    acc_sync_enabled: false,
    shop_phase: '',
    shop_cost_code: '',
    field_hrs_budgeted: null,
    shop_hrs_budgeted: null,
    total_hrs: null,
  });

  const [packageAreas, setPackageAreas] = useState([]);
  // The project's ACC asset sync (ticket #257): whether it is linked to ACC,
  // and whether packages in it can sync.
  const [projectAccAssetSync, setProjectAccAssetSync] = useState({
    linked: false,
    enabled: false,
  });
  const [trackingStatuses, setTrackingStatuses] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [trades, setTrades] = useState([]);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [activitiesMenuOpen, setActivitiesMenuOpen] = useState(false);

  const auth = useContext(AuthContext);

  const initializedRef = useRef(false);

  useEffect(() => {
    Logger.debug('PackageModal useEffect triggered', {
      open,
      editPackage,
      hasProject: !!currentProject,
      projectId: currentProject?.id,
    });

    // Reset initialization when modal closes or package changes
    if (!open) {
      initializedRef.current = false;
    }

    const getPackageOptions = async () => {
      try {
        if (!currentProject || !currentProject.id) {
          Logger.debug('No project selected, cannot get package options');
          setWorkflows([]);
          return;
        }

        const needsLiveDefaultTrade =
          !editPackage || !packageSelected?.trade_id;
        const liveDefaultTradePromise = needsLiveDefaultTrade
          ? ProjectsAPI.getActive(auth.token)
              .then((activeProjects) => {
                const match = (activeProjects || []).find(
                  (project) => project.id === currentProject.id
                );
                if (!match) return { ok: false };
                return {
                  ok: true,
                  defaultTradeId: match.default_trade_id || '',
                };
              })
              .catch((err) => {
                Logger.warn('Failed to refresh project default trade', err);
                return { ok: false };
              })
          : Promise.resolve({ ok: false });

        const resolveLiveDefaultTradeId = async () => {
          const result = await liveDefaultTradePromise;
          if (!result.ok) return currentProject.default_trade_id || '';
          return result.defaultTradeId;
        };

        // Pass packageId when editing so backend can filter statuses by the package's status step set
        const packageIdForFilter =
          editPackage && packageSelected?.id ? packageSelected.id : null;
        const options = await PackagesAPI.getPackageOptions(
          currentProject.id,
          auth.token,
          packageIdForFilter
        );
        const trackingList = options.trackingStatuses || [];
        const areaList = options.packageAreas || [];
        setPackageAreas(areaList);
        setTrackingStatuses(trackingList);
        const accAssetSync = options.accAssetSync || {
          linked: false,
          enabled: false,
        };
        setProjectAccAssetSync(accAssetSync);

        const tradesData = await TradesAPI.getAll(auth.token);
        setTrades(tradesData);

        // Fetch project workflows
        const workflowList = await ProjectsAPI.getWorkflow(
          currentProject.id,
          auth.token
        );
        setWorkflows(workflowList);

        // Only initialize state if not already initialized for this session
        if (!initializedRef.current) {
          if (editPackage) {
            // Get area_id for the selected package
            const currentAreaId = packageSelected.area_id;
            const currentTrackingStatusId =
              packageSelected.current_tracking_status_id;

            // Get selected workflow step set ID
            const selectedWorkflowId = packageSelected.status_step_set_id;

            // If the package doesn't have status_step_set_id, find the workflow containing currentTrackingStatusId
            let activeWorkflowId = selectedWorkflowId;
            if (!activeWorkflowId && currentTrackingStatusId) {
              const matchedWorkflow = workflowList.find((w) =>
                w.statuses?.some(
                  (s) => String(s.id) === String(currentTrackingStatusId)
                )
              );
              activeWorkflowId = matchedWorkflow?.id || '';
            }
            if (!activeWorkflowId) {
              const nativeWorkflow = workflowList.find((w) => w.is_native);
              activeWorkflowId =
                nativeWorkflow?.id || workflowList[0]?.id || '';
            }

            const currentWorkflow = workflowList.find(
              (w) => w.id === activeWorkflowId
            );
            const workflowStatuses = currentWorkflow
              ? currentWorkflow.statuses
              : [];
            const statusesToSearch =
              workflowStatuses.length > 0 ? workflowStatuses : trackingList;

            Logger.debug('Package modal initialized', {
              currentAreaId,
              areaListCount: areaList?.length,
              packageId: packageSelected?.id,
              activeWorkflowId,
            });

            // Find tracking status - handle both string and number IDs
            const trackingStatusIndex = statusesToSearch.findIndex((item) => {
              return (
                String(item.id) === String(currentTrackingStatusId) ||
                item.id === currentTrackingStatusId
              );
            });

            // Find area - handle both string and number IDs, and also check if area exists
            const areaIndex = areaList.findIndex((item) => {
              return (
                String(item.id) === String(currentAreaId) ||
                item.id === currentAreaId
              );
            });

            Logger.debug('Package indices', { trackingStatusIndex, areaIndex });

            // Get the area object, or null if not found
            const selectedArea = areaIndex >= 0 ? areaList[areaIndex] : null;
            let selectedTrackingStatus =
              trackingStatusIndex >= 0
                ? statusesToSearch[trackingStatusIndex]
                : null;

            // If current status is not in the list (e.g. it was from another project's workflow),
            // default to first status so the dropdown shows a valid value and user can save to fix
            if (!selectedTrackingStatus && statusesToSearch.length > 0) {
              selectedTrackingStatus = statusesToSearch[0];
              Logger.warn(
                'Package current status not in list (wrong workflow); defaulting to first status',
                {
                  currentTrackingStatusId: currentTrackingStatusId,
                  defaultStatusId: selectedTrackingStatus.id,
                }
              );
            }

            if (areaIndex < 0 && currentAreaId) {
              Logger.warn('Area not found in area list', {
                areaId: currentAreaId,
                availableAreas: areaList.map((a) => ({
                  id: a.id,
                  name: a.name,
                })),
              });
            }

            // Get the package's trade_id from the database
            let packageTradeId = packageSelected.trade_id || '';

            // If package trade_id is empty, copy from the project's current
            // default trade and save to database
            const projectTradeId = !packageTradeId
              ? await resolveLiveDefaultTradeId()
              : '';
            if (!packageTradeId && projectTradeId) {
              Logger.debug('Package trade_id is empty, copying from project:', {
                projectTradeId,
              });

              try {
                // Save the project's trade_id to the package in the database
                const updateData = {
                  id: packageSelected.id,
                  name: packageSelected.name,
                  area_id: packageSelected.area_id,
                  archived: packageSelected.archived,
                  current_tracking_status_id:
                    packageSelected.current_tracking_status_id,
                  project_id: packageSelected.project_id,
                  field_cost_code: packageSelected.field_cost_code,
                  field_phase: packageSelected.field_phase,
                  units: packageSelected.units,
                  uom: packageSelected.uom,
                  trade_id: projectTradeId,
                  is_general_conditions:
                    packageSelected.is_general_conditions || false,
                  is_change_order: packageSelected.is_change_order || false,
                  status_step_set_id: activeWorkflowId,
                };

                await PackagesAPI.update(updateData, auth.token);
                Logger.debug('Saved project trade_id to package in database:', {
                  projectTradeId,
                });
                packageTradeId = projectTradeId;

                // Notify parent component to update its packages list
                if (onTradeAutoCopied) {
                  onTradeAutoCopied(packageSelected.id, projectTradeId);
                }
              } catch (err) {
                Logger.error('Error saving trade to package', err);
                // If save fails, still show the project trade but don't persist
                packageTradeId = projectTradeId;
              }
            }

            Logger.debug('Package trade_id to display:', { packageTradeId });

            // Use activity_ids from backend (populated from package_activities table)
            const existingActivityIds = packageSelected.activity_ids || [];

            // Default include_activities to true if not set
            const includeActivities =
              packageSelected.include_activities !== false;

            setCurrentPackage({
              id: packageSelected.id,
              name: packageSelected.name,
              autodesk_name: packageSelected.autodesk_name || '',
              area: selectedArea,
              field_cost_code: packageSelected.field_cost_code,
              field_phase: packageSelected.field_phase,
              units: packageSelected.units,
              uom: packageSelected.uom,
              tracking_status: selectedTrackingStatus,
              status_step_set_id: activeWorkflowId,
              archived: packageSelected.archived,
              trade_id: packageTradeId,
              default_activity_id: packageSelected.default_activity_id || '',
              activity_ids: existingActivityIds,
              include_activities: includeActivities,
              requires_shop_fabrication:
                packageSelected.requires_shop_fabrication || false,
              requires_vdc_design: packageSelected.requires_vdc_design || false,
              is_general_conditions:
                packageSelected.is_general_conditions || false,
              is_change_order: packageSelected.is_change_order || false,
              acc_sync_enabled: packageSelected.acc_sync_enabled === true,
              shop_phase: packageSelected.shop_phase || '',
              shop_cost_code: packageSelected.shop_cost_code || '',
              field_hrs_budgeted: packageSelected.field_hrs_budgeted,
              shop_hrs_budgeted: packageSelected.shop_hrs_budgeted,
              total_hrs: packageSelected.total_hrs,
            });

            // Fetch available activities for the trade (always fetch to populate dropdown)
            const tradeIdToUse = packageTradeId;
            if (tradeIdToUse) {
              const shouldAutoSelect =
                existingActivityIds.length === 0 && includeActivities;
              // Pass package data so it can be saved to database when auto-selecting
              const packageDataForSave = shouldAutoSelect
                ? {
                    id: packageSelected.id,
                    name: packageSelected.name,
                    area_id: packageSelected.area_id,
                    archived: packageSelected.archived,
                    current_tracking_status_id:
                      packageSelected.current_tracking_status_id,
                    project_id: packageSelected.project_id,
                    field_cost_code: packageSelected.field_cost_code,
                    field_phase: packageSelected.field_phase,
                    units: packageSelected.units,
                    uom: packageSelected.uom,
                    trade_id: packageTradeId,
                    is_general_conditions:
                      packageSelected.is_general_conditions || false,
                    is_change_order: packageSelected.is_change_order || false,
                    status_step_set_id: activeWorkflowId,
                  }
                : null;
              fetchActivitiesForTrade(
                tradeIdToUse,
                shouldAutoSelect,
                packageDataForSave
              );
            }
          } else {
            // New package - inherit the project's current default trade
            const inheritedTradeId = await resolveLiveDefaultTradeId();

            // Determine default workflow
            const nativeWorkflow = workflowList.find((w) => w.is_native);
            const defaultWorkflow = nativeWorkflow || workflowList[0] || null;
            const defaultWorkflowId = defaultWorkflow ? defaultWorkflow.id : '';
            const defaultWorkflowStatuses = defaultWorkflow
              ? defaultWorkflow.statuses
              : [];
            const defaultTrackingStatus =
              defaultWorkflowStatuses.length > 0
                ? defaultWorkflowStatuses[0]
                : null;

            setCurrentPackage((prev) => ({
              ...prev,
              trade_id: inheritedTradeId,
              // New packages sync to ACC by default when the project allows it.
              acc_sync_enabled: accAssetSync.enabled,
              activity_ids: [],
              status_step_set_id: defaultWorkflowId,
              tracking_status: defaultTrackingStatus,
              field_hrs_budgeted: null,
              shop_hrs_budgeted: null,
              total_hrs: null,
            }));

            // Fetch activities for inherited trade and auto-select all
            if (inheritedTradeId) {
              fetchActivitiesForTrade(inheritedTradeId, true);
            }
          }
          initializedRef.current = true;
        }
      } catch (err) {
        Logger.error('Error getting package options', err);
        setTrades([]); // Set empty array on error to prevent map error
        const errorMessage = err.response?.data?.message || err.message;
        alert(errorMessage);
      }
    };
    if (open && currentProject && currentProject.id) {
      getPackageOptions();
    }
  }, [open, editPackage, packageSelected, auth.token, currentProject]);

  useEffect(() => {
    if (open && editPackage && packageSelected) {
      setCurrentPackage((prev) => ({
        ...prev,
        field_hrs_budgeted: packageSelected.field_hrs_budgeted,
        shop_hrs_budgeted: packageSelected.shop_hrs_budgeted,
        total_hrs: packageSelected.total_hrs,
      }));
    }
  }, [
    packageSelected?.field_hrs_budgeted,
    packageSelected?.shop_hrs_budgeted,
    packageSelected?.total_hrs,
    open,
    editPackage,
  ]);

  // Fetch activities when trade changes
  // If autoSelectAll is true, automatically select all activities and save to database
  // packageData is optional - if provided, will save to database
  const fetchActivitiesForTrade = async (
    tradeId,
    autoSelectAll = false,
    packageData = null
  ) => {
    if (!tradeId) {
      setAvailableActivities([]);
      return;
    }

    try {
      const activitiesData = await TradesAPI.getActivitiesByTradeId(
        tradeId,
        auth.token
      );
      Logger.debug('Fetched activities for trade', {
        tradeId,
        count: activitiesData.length,
      });
      setAvailableActivities(activitiesData);

      // Auto-select all activities if requested (using IDs), excluding 'Default'
      if (autoSelectAll && activitiesData.length > 0) {
        const allActivityIds = activitiesData
          .filter((activity) => activity.name.toLowerCase() !== 'default')
          .map((activity) => activity.id);
        Logger.debug('Auto-selecting all activity IDs', {
          activityIds: allActivityIds,
        });
        setCurrentPackage((prev) => ({
          ...prev,
          activity_ids: allActivityIds,
        }));

        // Save to database if packageData is provided (existing package)
        if (packageData && packageData.id) {
          try {
            const updateData = {
              id: packageData.id,
              name: packageData.name,
              area_id: packageData.area_id,
              archived: packageData.archived,
              current_tracking_status_id:
                packageData.current_tracking_status_id,
              project_id: packageData.project_id,
              field_cost_code: packageData.field_cost_code,
              field_phase: packageData.field_phase,
              units: packageData.units,
              uom: packageData.uom,
              trade_id: packageData.trade_id || '',
              activity_ids: allActivityIds,
              is_general_conditions: packageData.is_general_conditions || false,
              is_change_order: packageData.is_change_order || false,
            };

            await PackagesAPI.update(updateData, auth.token);
            Logger.debug('Saved auto-selected activities to database', {
              packageId: packageData.id,
              activityIds: allActivityIds,
            });

            // Notify parent component to update its packages list
            if (onActivitiesAutoCopied) {
              onActivitiesAutoCopied(packageData.id, allActivityIds);
            }
          } catch (err) {
            Logger.error(
              'Error saving auto-selected activities to database',
              err
            );
          }
        }
      }
    } catch (err) {
      Logger.error('Error fetching activities for trade', err);
      setAvailableActivities([]);
    }
  };

  const handleChange = (e) => {
    const itemChanged = e.target.name;

    if (itemChanged === 'status_step_set_id') {
      const newWorkflowId = e.target.value;
      const targetWorkflow = workflows.find((w) => w.id === newWorkflowId);
      const firstStatusOfNewWorkflow = targetWorkflow?.statuses?.[0] || null;

      if (editPackage) {
        const confirmSwitch = window.confirm(
          'Switching workflows will move this package to the first status of the new workflow. Continue?'
        );
        if (!confirmSwitch) {
          return;
        }
      }

      setCurrentPackage((prev) => ({
        ...prev,
        status_step_set_id: newWorkflowId,
        tracking_status: firstStatusOfNewWorkflow,
      }));
      return;
    }

    let valueChanged;

    if (
      itemChanged === 'in_stratus' ||
      itemChanged === 'archived' ||
      itemChanged === 'requires_shop_fabrication' ||
      itemChanged === 'requires_vdc_design' ||
      itemChanged === 'is_general_conditions' ||
      itemChanged === 'is_change_order' ||
      itemChanged === 'acc_sync_enabled'
    ) {
      valueChanged = e.target.checked;

      // Once the saved package is syncing, the flag cannot be turned off.
      if (
        itemChanged === 'acc_sync_enabled' &&
        !valueChanged &&
        editPackage &&
        packageSelected?.acc_sync_enabled === true
      ) {
        return;
      }

      // If turning off shop fabrication, clear shop fields
      if (itemChanged === 'requires_shop_fabrication' && !e.target.checked) {
        const changePackage = {
          ...currentPackage,
          requires_shop_fabrication: false,
          shop_phase: '',
          shop_cost_code: '',
        };
        setCurrentPackage(changePackage);
        return;
      }
    } else if (itemChanged === 'include_activities') {
      // Handle direct include_activities checkbox
      const newIncludeActivities = e.target.checked;
      const existingActivityIds = currentPackage.activity_ids || [];

      let changePackage = {
        ...currentPackage,
        include_activities: newIncludeActivities,
        // Keep existing activities when toggling
        activity_ids: existingActivityIds,
      };

      // When turning OFF track activities, auto-select "Default" as the default_activity_id
      if (!newIncludeActivities && availableActivities.length > 0) {
        const defaultActivity = availableActivities.find(
          (a) => a.name.toLowerCase() === 'default'
        );
        if (defaultActivity) {
          changePackage.default_activity_id = defaultActivity.id;
        }
      }

      Logger.debug('Include activities changed', {
        newIncludeActivities,
        defaultActivityId: changePackage.default_activity_id || null,
      });
      setCurrentPackage(changePackage);

      // If toggling to include activities and there's a trade, fetch activities
      if (newIncludeActivities && currentPackage.trade_id) {
        // Auto-select all activities if package doesn't have any saved
        const shouldAutoSelect = existingActivityIds.length === 0;
        fetchActivitiesForTrade(currentPackage.trade_id, shouldAutoSelect);
      }
      return;
    } else if (itemChanged === 'activity_ids') {
      // Handle multi-select for activities - value is already an array
      // Special options (SELECT_ALL, DESELECT_ALL) are handled in the Select's onChange
      valueChanged =
        typeof e.target.value === 'string'
          ? e.target.value.split(',')
          : e.target.value;
    } else {
      valueChanged = e.target.value;
    }

    Logger.debug('handleChange called', {
      itemChanged,
      value: valueChanged,
    });

    let changePackage = { ...currentPackage, [itemChanged]: valueChanged };

    // General Conditions and Change Order are mutually exclusive.
    if (itemChanged === 'is_general_conditions' && valueChanged === true) {
      changePackage.is_change_order = false;
    }
    if (itemChanged === 'is_change_order' && valueChanged === true) {
      changePackage.is_general_conditions = false;
    }

    // New General Conditions and Change Order packages do not sync to ACC by
    // default (ticket #257); others do when the project allows it.
    if (
      !editPackage &&
      (itemChanged === 'is_general_conditions' ||
        itemChanged === 'is_change_order')
    ) {
      changePackage.acc_sync_enabled =
        projectAccAssetSync.enabled &&
        !changePackage.is_general_conditions &&
        !changePackage.is_change_order;
    }

    // If trade_id changed, fetch activities
    if (itemChanged === 'trade_id') {
      changePackage.activity_ids = []; // Reset first, will be populated by fetchActivitiesForTrade
      // Fetch activities and auto-select all
      if (currentPackage.include_activities) {
        fetchActivitiesForTrade(valueChanged, true);
      }
    }

    Logger.debug('New package state', { package: changePackage });
    setCurrentPackage(changePackage);
  };

  const handleClose = () => {
    setCurrentPackage({
      id: '',
      name: '',
      autodesk_name: '',
      area: '',
      tracking_status: '',
      status_step_set_id: '',
      archived: false,
      field_cost_code: '',
      field_phase: '',
      units: 0,
      uom: '',
      trade_id: '',
      include_activities: true,
      activity_ids: [],
      default_activity_id: '',
      requires_shop_fabrication: false,
      requires_vdc_design: false,
      is_general_conditions: false,
      is_change_order: false,
      acc_sync_enabled: false,
      shop_phase: '',
      shop_cost_code: '',
      field_hrs_budgeted: null,
      shop_hrs_budgeted: null,
      total_hrs: null,
    });
    setAvailableActivities([]);
    handleCancel();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    Logger.userAction('Submitting package', { package: currentPackage });

    // Validation based on tracking mode
    if (currentPackage.include_activities) {
      // When tracking is ON, require at least one activity selected
      if (
        !currentPackage.activity_ids ||
        currentPackage.activity_ids.length === 0
      ) {
        alert('Please select at least one activity for this package.');
        return;
      }
    } else {
      // When tracking is OFF, require default activity
      if (!currentPackage.default_activity_id) {
        alert('Please select a default activity for this package.');
        return;
      }
    }

    // Validation for shop fabrication fields
    if (currentPackage.requires_shop_fabrication) {
      if (!currentPackage.shop_phase || !currentPackage.shop_phase.trim()) {
        alert('Please enter a Shop Phase when Shop Fabrication is required.');
        return;
      }
      if (
        !currentPackage.shop_cost_code ||
        !currentPackage.shop_cost_code.trim()
      ) {
        alert(
          'Please enter a Shop Cost Code when Shop Fabrication is required.'
        );
        return;
      }
    }

    editPackage
      ? handleUpdatePackage(currentPackage)
      : handleAddPackage(currentPackage);

    setCurrentPackage({
      id: '',
      name: '',
      autodesk_name: '',
      area: '',
      tracking_status: '',
      status_step_set_id: '',
      archived: false,
      field_cost_code: '',
      field_phase: '',
      units: 0,
      uom: '',
      trade_id: '',
      activity_ids: [],
      include_activities: true,
      default_activity_id: '',
      requires_shop_fabrication: false,
      requires_vdc_design: false,
      is_general_conditions: false,
      is_change_order: false,
      acc_sync_enabled: false,
      shop_phase: '',
      shop_cost_code: '',
      field_hrs_budgeted: null,
      shop_hrs_budgeted: null,
      total_hrs: null,
    });
    setAvailableActivities([]);
  };

  const currentWorkflow = workflows.find(
    (w) => w.id === currentPackage.status_step_set_id
  );
  const showWorkflowDropdown =
    workflows.length > 1 &&
    (!editPackage || !packageSelected?.autodesk_asset_id);

  // Lock only when the saved package already has acc_sync_enabled. A new
  // package defaults the box on, but that value is not persisted yet.
  const accSyncLockedOn =
    editPackage && packageSelected?.acc_sync_enabled === true;

  const statusOptions =
    currentWorkflow && currentWorkflow.statuses?.length > 0
      ? currentWorkflow.statuses
      : trackingStatuses;

  return (
    <div>
      <Dialog open={open}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editPackage ? 'Edit Package' : 'New Package'}
            {editPackage &&
            packageSelected?.autodesk_asset_id &&
            packageSelected?.autodesk_project_id ? (
              <Tooltip
                title={
                  packageSelected.autodesk_last_synced
                    ? `View in Autodesk Construction Cloud — Last synced: ${new Date(packageSelected.autodesk_last_synced).toLocaleString()}`
                    : 'View in Autodesk Construction Cloud'
                }
                arrow
              >
                <Link
                  href={`https://acc.autodesk.com/build/assets/projects/${packageSelected.autodesk_project_id}/assets?assetId=${packageSelected.autodesk_asset_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'primary.main', ml: 0.5, fontSize: '0.875rem' }}
                >
                  View in ACC
                </Link>
              </Tooltip>
            ) : editPackage ? (
              <Tooltip title="Not yet synced to ACC" arrow>
                <Typography
                  component="span"
                  sx={{ ml: 0.5, fontSize: '0.875rem', color: 'text.disabled' }}
                >
                  View in ACC
                </Typography>
              </Tooltip>
            ) : null}
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 1, pb: 1 }}>
            <TextField
              autoFocus
              fullWidth
              margin="normal"
              id="autodesk_name"
              label="Name"
              name="autodesk_name"
              autoComplete="off"
              type="text"
              value={currentPackage.autodesk_name || ''}
              onChange={handleChange}
              sx={{ mt: 0 }}
            />
            <TextField
              required
              fullWidth
              margin="normal"
              id="name"
              label="Description"
              name="name"
              autoComplete="off"
              type="text"
              value={currentPackage.name}
              onChange={handleChange}
            />
            <FormControl sx={{ mt: 1, mb: 1, minWidth: 550 }}>
              <InputLabel id="area">Area</InputLabel>
              <Select
                required
                labelId="area"
                id="area"
                value={currentPackage.area}
                name="area"
                label="area"
                onChange={handleChange}
              >
                {packageAreas.map((area) => (
                  <MenuItem key={area.id} value={area}>
                    {area.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Package flags: VDC, shop fabrication, general conditions */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.25,
                mt: 1,
                mb: 0.5,
              }}
            >
              <FormControlLabel
                sx={{ mr: 0, ml: 0 }}
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={currentPackage.requires_vdc_design}
                    name="requires_vdc_design"
                  />
                }
                label="Requires VDC Design"
                labelPlacement="end"
              />
              <FormControlLabel
                sx={{ mr: 0, ml: 0 }}
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={currentPackage.requires_shop_fabrication}
                    name="requires_shop_fabrication"
                  />
                }
                label="Requires Shop Fabrication"
                labelPlacement="end"
              />
              <FormControlLabel
                sx={{ mr: 0, ml: 0 }}
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={currentPackage.is_general_conditions}
                    name="is_general_conditions"
                  />
                }
                label="General Conditions"
                labelPlacement="end"
              />
              <FormControlLabel
                sx={{ mr: 0, ml: 0 }}
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={currentPackage.is_change_order}
                    name="is_change_order"
                  />
                }
                label="Change Order"
                labelPlacement="end"
              />
              {projectAccAssetSync.linked && (
                <Tooltip
                  title={
                    !projectAccAssetSync.enabled
                      ? 'ACC asset sync is turned off for this project'
                      : accSyncLockedOn
                        ? 'Sync to ACC cannot be turned off once enabled'
                        : ''
                  }
                  arrow
                >
                  <span>
                    <FormControlLabel
                      sx={{ mr: 0, ml: 0 }}
                      control={
                        <Checkbox
                          onChange={handleChange}
                          checked={currentPackage.acc_sync_enabled === true}
                          name="acc_sync_enabled"
                          disabled={
                            !projectAccAssetSync.enabled || accSyncLockedOn
                          }
                        />
                      }
                      label="Sync to ACC"
                      labelPlacement="end"
                    />
                  </span>
                </Tooltip>
              )}
            </Box>

            {/* Field Phase and Field Cost Code */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                color: 'secondary',
              }}
            >
              <TextField
                required
                margin="normal"
                id="field_phase"
                label="Field Phase"
                name="field_phase"
                autoComplete="off"
                type="text"
                value={currentPackage.field_phase}
                onChange={handleChange}
              />
              <TextField
                required
                margin="normal"
                id="field_cost_code"
                label="Field Cost Code"
                name="field_cost_code"
                autoComplete="off"
                type="text"
                value={currentPackage.field_cost_code}
                onChange={handleChange}
                sx={{
                  ml: 2,
                }}
              />
            </Box>

            {/* Conditional Shop Fields - Only show when Shop Fabrication is checked */}
            {currentPackage.requires_shop_fabrication && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  mb: 1,
                }}
              >
                <TextField
                  required
                  margin="dense"
                  id="shop_phase"
                  label="Shop Phase"
                  name="shop_phase"
                  autoComplete="off"
                  type="text"
                  value={currentPackage.shop_phase}
                  onChange={handleChange}
                />
                <TextField
                  required
                  margin="dense"
                  id="shop_cost_code"
                  label="Shop Cost Code"
                  name="shop_cost_code"
                  autoComplete="off"
                  type="text"
                  value={currentPackage.shop_cost_code}
                  onChange={handleChange}
                  sx={{ ml: 2 }}
                />
              </Box>
            )}

            {/* Units and UOM - Hidden for now, may be used in the future */}
            <Box
              sx={{
                width: '100%',
                display: 'none', // Hidden - may be used in the future
                flexDirection: 'row',
                justifyContent: 'flex-start',
                color: 'secondary',
              }}
            >
              <TextField
                margin="normal"
                id="units"
                label="Units"
                name="units"
                autoComplete="off"
                type="text"
                value={currentPackage.units}
                onChange={handleChange}
              />
              <FormControl
                sx={{
                  m: 2,
                  minWidth: 100,
                }}
              >
                <InputLabel id="uom">UOM</InputLabel>
                <Select
                  labelId="uom"
                  id="uom"
                  value={currentPackage.uom}
                  name="uom"
                  label="uom"
                  onChange={handleChange}
                >
                  <MenuItem key="ea" value="ea">
                    ea
                  </MenuItem>
                  <MenuItem key="ft" value="ft">
                    ft
                  </MenuItem>
                  <MenuItem key="in" value="in">
                    in
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {editPackage && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 1,
                  mb: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  width: '100%',
                  maxWidth: 550,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Budgeted Hours
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                    Field:{' '}
                    <strong>
                      {currentPackage.field_hrs_budgeted !== null &&
                      currentPackage.field_hrs_budgeted !== undefined
                        ? Number(currentPackage.field_hrs_budgeted).toFixed(1)
                        : '0.0'}
                    </strong>{' '}
                    | Shop:{' '}
                    <strong>
                      {currentPackage.shop_hrs_budgeted !== null &&
                      currentPackage.shop_hrs_budgeted !== undefined
                        ? Number(currentPackage.shop_hrs_budgeted).toFixed(1)
                        : '0.0'}
                    </strong>{' '}
                    | Total:{' '}
                    <strong>
                      {currentPackage.total_hrs !== null &&
                      currentPackage.total_hrs !== undefined
                        ? Number(currentPackage.total_hrs).toFixed(1)
                        : '0.0'}
                    </strong>
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onOpenUpdateBudget(currentPackage)}
                >
                  Edit Budget
                </Button>
              </Box>
            )}

            {showWorkflowDropdown && (
              <FormControl sx={{ mt: 1, mb: 1, minWidth: 550 }}>
                <InputLabel id="status_step_set_id">Workflow</InputLabel>
                <Select
                  required
                  labelId="status_step_set_id"
                  id="status_step_set_id"
                  value={currentPackage.status_step_set_id || ''}
                  name="status_step_set_id"
                  label="Workflow"
                  onChange={handleChange}
                >
                  {workflows.map((wf) => (
                    <MenuItem key={wf.id} value={wf.id}>
                      {wf.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl sx={{ mt: 1, mb: 1, minWidth: 550 }}>
              <InputLabel id="tracking_status">Status</InputLabel>
              <Select
                required
                labelId="tracking_status"
                id="tracking_status"
                value={currentPackage.tracking_status || ''}
                name="tracking_status"
                label="Status"
                onChange={handleChange}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.id} value={status}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 1,
              }}
            >
              <TextField
                required
                select
                margin="normal"
                id="trade_id"
                label="Trade"
                name="trade_id"
                value={currentPackage.trade_id || ''}
                onChange={handleChange}
                sx={{ width: '35%' }}
              >
                {(trades || []).map((trade) => (
                  <MenuItem key={trade.id} value={trade.id}>
                    {trade.name}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={currentPackage.archived}
                  />
                }
                label="Archived"
                labelPlacement="start"
                id="archived"
                name="archived"
              />
            </Box>

            {/* Activity Tracking Section */}
            <Box sx={{ mt: 0.5, borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={currentPackage.include_activities}
                    name="include_activities"
                  />
                }
                label={
                  <Typography fontWeight="bold">
                    Track work activities for this package
                  </Typography>
                }
                labelPlacement="end"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', ml: 4, mt: -0.5 }}
              >
                {currentPackage.include_activities
                  ? 'Select which activities foremen can choose from when entering time.'
                  : 'All time entries for this package will be charged to the activity selected below. The activity dropdown will not appear in timecard entry.'}
              </Typography>

              {/* WHEN TRACK ACTIVITIES = TRUE */}
              {currentPackage.include_activities && (
                <>
                  {/* Section Header with Button Group */}
                  <Box sx={{ mt: 1, mb: 1 }}>
                    {/* Activity Count - excluding 'Default' */}
                    <Typography variant="body2" color="text.secondary">
                      {
                        (currentPackage.activity_ids || []).filter((id) => {
                          const activity = availableActivities.find(
                            (a) => a.id === id
                          );
                          return (
                            activity &&
                            activity.name.toLowerCase() !== 'default'
                          );
                        }).length
                      }{' '}
                      activities selected
                    </Typography>
                  </Box>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="activities-label">Activities</InputLabel>
                    <Select
                      labelId="activities-label"
                      id="activity_ids"
                      multiple
                      name="activity_ids"
                      value={currentPackage.activity_ids || []}
                      open={activitiesMenuOpen}
                      onClose={() => setActivitiesMenuOpen(false)}
                      onOpen={() => setActivitiesMenuOpen(true)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 1400,
                          },
                        },
                      }}
                      onChange={(e) => {
                        const selectedValue =
                          typeof e.target.value === 'string'
                            ? e.target.value.split(',')
                            : e.target.value;

                        // Check for special "Select All" or "Deselect All" options
                        if (selectedValue.includes('SELECT_ALL')) {
                          // Select all available activities (using IDs)
                          const allActivityIds = availableActivities.map(
                            (activity) => activity.id
                          );
                          setCurrentPackage((prev) => ({
                            ...prev,
                            activity_ids: allActivityIds,
                          }));
                        } else if (selectedValue.includes('DESELECT_ALL')) {
                          // Deselect all activities
                          setCurrentPackage((prev) => ({
                            ...prev,
                            activity_ids: [],
                          }));
                        } else if (selectedValue.includes('DONE')) {
                          // Close the menu when "Done" is clicked
                          setActivitiesMenuOpen(false);
                        } else {
                          // Remove special options from the selection if they were clicked
                          const filteredValue = selectedValue.filter(
                            (val) =>
                              val !== 'SELECT_ALL' &&
                              val !== 'DESELECT_ALL' &&
                              val !== 'DONE'
                          );
                          handleChange({
                            ...e,
                            target: {
                              ...e.target,
                              value: filteredValue,
                            },
                          });
                        }
                      }}
                      input={<OutlinedInput label="Work Activities" />}
                      renderValue={(selected) => {
                        // Only show chips for activities that have loaded names
                        // Filter out 'Default' activity and any IDs where we can't find the name yet
                        const chips = selected
                          .map((id) => {
                            const activity = availableActivities.find(
                              (a) => a.id === id
                            );
                            return activity
                              ? { id, name: activity.name }
                              : null;
                          })
                          .filter(Boolean)
                          .filter(
                            (chip) => chip.name.toLowerCase() !== 'default'
                          );

                        if (chips.length === 0 && selected.length > 0) {
                          // Activities selected but not loaded yet - show count
                          return `${selected.length} activities selected`;
                        }

                        return (
                          <Box
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          >
                            {chips.map((chip) => (
                              <Chip
                                key={chip.id}
                                label={chip.name}
                                size="small"
                              />
                            ))}
                          </Box>
                        );
                      }}
                      disabled={
                        !currentPackage.trade_id ||
                        availableActivities.length === 0
                      }
                    >
                      {/* Sticky Header using ListSubheader */}
                      <ListSubheader
                        sx={{
                          py: 1.5,
                          backgroundColor: 'grey.200',
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: 'rgba(0, 0, 0, 0.87)',
                          lineHeight: 1.5,
                        }}
                      >
                        Select the work activities to track for this package
                      </ListSubheader>
                      <MenuItem
                        value="SELECT_ALL"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        }}
                      >
                        <ListItemText primary="Select All" />
                      </MenuItem>
                      <MenuItem
                        value="DESELECT_ALL"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        }}
                      >
                        <ListItemText primary="Deselect All" />
                      </MenuItem>
                      <Divider />

                      {/* Scrollable Activities Container with manual click handling */}
                      <Box sx={{ maxHeight: 700, overflowY: 'auto' }}>
                        {/* Filter out 'Default' activity - it's only for non-tracked mode */}
                        {availableActivities
                          .filter(
                            (activity) =>
                              activity.name.toLowerCase() !== 'default'
                          )
                          .map((activity) => {
                            const isSelected = (
                              currentPackage.activity_ids || []
                            ).includes(activity.id);
                            return (
                              <MenuItem
                                key={activity.id}
                                value={activity.id}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Manually toggle selection
                                  const currentIds =
                                    currentPackage.activity_ids || [];
                                  const newIds = isSelected
                                    ? currentIds.filter(
                                        (id) => id !== activity.id
                                      )
                                    : [...currentIds, activity.id];
                                  setCurrentPackage((prev) => ({
                                    ...prev,
                                    activity_ids: newIds,
                                  }));
                                }}
                                sx={{
                                  backgroundColor: 'transparent',
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                  fontWeight: isSelected ? 600 : 400,
                                  py: 1,
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  sx={{
                                    padding: 0,
                                    marginRight: 1.5,
                                    color: isSelected
                                      ? 'primary.main'
                                      : 'action.disabled',
                                    '&.Mui-checked': {
                                      color: 'primary.main',
                                    },
                                  }}
                                />
                                <ListItemText
                                  primary={activity.name}
                                  sx={{
                                    color: isSelected
                                      ? 'primary.dark'
                                      : 'text.primary',
                                    '& .MuiListItemText-primary': {
                                      fontWeight: isSelected ? 600 : 400,
                                    },
                                  }}
                                />
                              </MenuItem>
                            );
                          })}
                      </Box>

                      <Divider />
                      {/* Done Button */}
                      <MenuItem
                        value="DONE"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActivitiesMenuOpen(false);
                        }}
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          justifyContent: 'center',
                          mx: 2,
                          my: 1,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        }}
                      >
                        Done
                      </MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              {/* Default Activity Dropdown - Only show when tracking is OFF */}
              {currentPackage.include_activities ? (
                /* When tracking is ON - Just show validation message, no default activity needed */
                <>
                  {(currentPackage.activity_ids || []).length === 0 &&
                    currentPackage.trade_id && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        At least one activity must be selected
                      </Typography>
                    )}
                </>
              ) : (
                /* When tracking is OFF - Show single activity in distinct visual style */
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: 'info.lighter',
                    borderRadius: 1,
                  }}
                >
                  <TextField
                    required
                    select
                    fullWidth
                    id="default_activity_id"
                    label="Activity"
                    name="default_activity_id"
                    value={currentPackage.default_activity_id || ''}
                    onChange={handleChange}
                    disabled={
                      !currentPackage.trade_id ||
                      availableActivities.length === 0
                    }
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      },
                    }}
                  >
                    {/* Show all activities for the trade in Single Activity Mode */}
                    {(availableActivities || []).map((activity) => (
                      <MenuItem key={activity.id} value={activity.id}>
                        {activity.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button type="submit" variant="contained" sx={{ width: 180 }}>
              {editPackage ? 'Update Package' : 'Add Package'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleClose}
              sx={{ width: '25%' }}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};
